import type { QueryClient, UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { RenameTagV1WorkspacesWorkspaceIdTagsTagIdPatchError as RenameTagError } from '../../generated';
import type { TagView } from '../../generated';
import { createTagV1WorkspacesWorkspaceIdTagsPost as createTag } from '../../generated';
import { deleteTagV1WorkspacesWorkspaceIdTagsTagIdDelete as deleteTag } from '../../generated';
import { renameTagV1WorkspacesWorkspaceIdTagsTagIdPatch as renameTag } from '../../generated';
import { getAPIErrorCode } from '../../utils';
import { refetchImagePayloads } from '../set-asset-favorite/set-asset-favorite';
import { MAX_TAG_NAME_LENGTH } from '../set-image-tag/set-image-tag';

const TAG_ERROR_MESSAGES: Record<string, string> = {
  TAG_ALREADY_EXISTS: 'A tag with this name already exists.',
  INVALID_TAG_NAME: `Tag names must be 1 to ${MAX_TAG_NAME_LENGTH} characters.`,
  TAG_NOT_FOUND: 'This tag no longer exists.',
};

const GENERIC_TAG_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Maps a tag API error to the copy shown in the Tag Manager (inline in dialogs, or in a toast).
 */
export function getTagErrorMessage(error: unknown): string {
  const code = getAPIErrorCode(error);
  return (code && TAG_ERROR_MESSAGES[code]) || GENERIC_TAG_ERROR_MESSAGE;
}

export function isTagNotFoundError(error: unknown): boolean {
  return getAPIErrorCode(error) === 'TAG_NOT_FOUND';
}

function refreshTags(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ['ListTags'] });
}

export interface CreateTagMutationVariables {
  workspace_id: string;
  name: string;
}

export const useCreateTagMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<TagView, TError, CreateTagMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<TagView, TError, CreateTagMutationVariables, TContext>({
    mutationKey: ['CreateTag'],
    mutationFn: async ({ workspace_id, name }) => {
      const { data } = await createTag({ path: { workspace_id }, body: { name }, throwOnError: true });
      return data;
    },
    ...options,
    onSettled: (data, error, variables, context) => {
      options?.onSettled?.(data, error, variables, context);
      refreshTags(queryClient);
    },
  });
};

export interface RenameTagMutationVariables {
  workspace_id: string;
  tag_id: string;
  name: string;
}

export const useRenameTagMutation = <TError = RenameTagError, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<TagView, TError, RenameTagMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<TagView, TError, RenameTagMutationVariables, TContext>({
    mutationKey: ['RenameTag'],
    mutationFn: async ({ workspace_id, tag_id, name }) => {
      const { data } = await renameTag({ path: { workspace_id, tag_id }, body: { name }, throwOnError: true });
      return data;
    },
    ...options,
    onSettled: (data, error, variables, context) => {
      options?.onSettled?.(data, error, variables, context);
      refreshTags(queryClient);
      // Not returned: awaiting it would hold the dialog open until every image list has refetched.
      void refetchImagePayloads(queryClient, error);
    },
  });
};

export interface DeleteTagMutationVariables {
  workspace_id: string;
  tag_id: string;
}

export const useDeleteTagMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<UseMutationOptions<void, TError, DeleteTagMutationVariables, TContext>, 'mutationKey' | 'mutationFn'>,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, TError, DeleteTagMutationVariables, TContext>({
    mutationKey: ['DeleteTag'],
    mutationFn: async ({ workspace_id, tag_id }) => {
      await deleteTag({ path: { workspace_id, tag_id }, throwOnError: true });
    },
    ...options,
    onSettled: (data, error, variables, context) => {
      options?.onSettled?.(data, error, variables, context);
      refreshTags(queryClient);
      // Not returned: awaiting it would hold the dialog open until every image list has refetched.
      void refetchImagePayloads(queryClient, error);
    },
  });
};
